import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { api } from "@/api";
import { formatError } from "@/components/shared/helpers";
import { useTranslation } from "@/components/shared/i18n";
import { PasswordStrengthChecklist, isPasswordStrong } from "@/components/shared/PasswordRules";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast({ title: t("common.error"), description: t("reset.mismatch"), variant: "destructive" });
      return;
    }
    if (!isPasswordStrong(password)) {
      toast({ title: t("common.error"), description: "Le mot de passe doit contenir 8 caractères, 1 majuscule et 1 chiffre.", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, new_password: password });
      toast({ title: t("reset.success"), description: t("reset.success_desc") });
      navigate("/login");
    } catch (err) {
      toast({ title: t("reset.error"), description: formatError(err), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#060A14] text-slate-50 relative">
        <div className="absolute inset-x-0 top-0 h-[380px] bg-[radial-gradient(ellipse_at_top,rgba(56,189,248,0.28),transparent_60%),radial-gradient(ellipse_at_top_left,rgba(125,211,252,0.18),transparent_55%),radial-gradient(ellipse_at_bottom_right,rgba(59,130,246,0.12),transparent_55%)]" aria-hidden />
        <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-12">
          <Card className="w-full max-w-sm border-white/10 bg-[#0b0d12]/80 backdrop-blur">
            <CardContent className="pt-6">
              <div data-testid="reset-no-token" className="text-center text-slate-300">{t("reset.no_token")}</div>
              <div className="text-center mt-4">
                <a href="/login" className="text-sm text-violet-400 hover:underline" onClick={(e) => { e.preventDefault(); navigate("/login"); }}>
                  {t("reset.back")}
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#060A14] text-slate-50 relative">
      <div className="absolute inset-x-0 top-0 h-[380px] bg-[radial-gradient(ellipse_at_top,rgba(56,189,248,0.28),transparent_60%),radial-gradient(ellipse_at_top_left,rgba(125,211,252,0.18),transparent_55%),radial-gradient(ellipse_at_bottom_right,rgba(59,130,246,0.12),transparent_55%)]" aria-hidden />
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-12">
        <Card data-testid="reset-password-card" className="w-full max-w-sm border-white/10 bg-[#0b0d12]/80 backdrop-blur">
          <CardHeader>
            <CardTitle data-testid="reset-password-title" className="text-center text-slate-50">{t("reset.title")}</CardTitle>
            <div data-testid="reset-password-subtitle" className="text-sm text-slate-300 text-center">{t("reset.subtitle")}</div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1">
                <Label data-testid="reset-password-label">{t("reset.new_password")}</Label>
                <Input data-testid="reset-password-input" type="password" className="border-white/10 bg-white/[0.08]" value={password} onChange={(e) => setPassword(e.target.value)} required />
                <PasswordStrengthChecklist password={password} testIdPrefix="reset-pwd" />
              </div>
              <div className="space-y-1">
                <Label data-testid="reset-confirm-label">{t("reset.confirm")}</Label>
                <Input data-testid="reset-confirm-input" type="password" className="border-white/10 bg-white/[0.08]" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
              </div>
              <Button data-testid="reset-submit-button" type="submit" className="w-full rounded-full bg-violet-500 text-white hover:bg-violet-400" disabled={loading}>
                {loading ? t("reset.loading") : t("reset.submit")}
              </Button>
              <div className="text-center">
                <a data-testid="reset-back-link" href="/login" className="text-sm text-violet-400 hover:underline" onClick={(e) => { e.preventDefault(); navigate("/login"); }}>
                  {t("reset.back")}
                </a>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
